'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const tutors = [
    { name: 'Dr. Kwame Mensah', subject: 'Mathematics', rating: '4.9', sessions: '320+', badge: 'Top Tutor', initials: 'KM', color: BRAND },
    { name: 'Ms. Abena Adjei', subject: 'Physics & Chemistry', rating: '4.8', sessions: '210+', badge: 'Verified', initials: 'AA', color: '#10B981' },
    { name: 'Mr. Kofi Boateng', subject: 'English Language', rating: '4.9', sessions: '450+', badge: 'Top Tutor', initials: 'KB', color: '#F59E0B' },
    { name: 'Ms. Ama Asante', subject: 'History & Social Studies', rating: '4.7', sessions: '180+', badge: 'Verified', initials: 'AA', color: '#A855F7' },
];

const howItWorks = [
    { step: '01', title: 'Search by subject', desc: 'Filter tutors by subject, grade level, availability, and teaching style.' },
    { step: '02', title: 'Review their profile', desc: 'See ratings, session history, qualifications, and student reviews.' },
    { step: '03', title: 'Book a session', desc: 'Pick a time that works for you and confirm your booking instantly.' },
    { step: '04', title: 'Start learning', desc: 'Join your session and get the focused, one-on-one attention you need.' },
];

export default function LearnTutors() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="tutors" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#FAFAFA' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 20% 60%, rgba(55,87,255,0.05) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        Tutor Connection
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            The right tutor,
                            <br />
                            <span style={{ color: BRAND }}>exactly when you need one.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Browse qualified tutors, review their profiles, and book sessions that fit your schedule. Direct, personal, and built around how you learn.
                        </p>
                    </div>
                </div>

                {/* Tutor cards */}
                <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {tutors.map((tutor, i) => (
                        <div
                            key={tutor.name}
                            className="rounded-2xl p-6 cursor-pointer transition-all duration-300"
                            style={{ background: 'white', border: '1px solid rgba(55,87,255,0.08)', transitionDelay: `${i * 60}ms` }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.25)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-6px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 16px 48px rgba(55,87,255,0.1)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div
                                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-800 text-white"
                                    style={{ background: tutor.color }}
                                >
                                    {tutor.initials}
                                </div>
                                <span
                                    className="text-xs font-700 px-2.5 py-1 rounded-full"
                                    style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}
                                >
                                    {tutor.badge}
                                </span>
                            </div>
                            <p className="text-sm font-800 mb-1" style={{ color: '#0D0D0D' }}>{tutor.name}</p>
                            <p className="text-xs font-600 mb-4" style={{ color: '#888' }}>{tutor.subject}</p>
                            <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(55,87,255,0.08)' }}>
                                <div className="flex items-center gap-1.5">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5 4.5 4z" fill={BRAND} /></svg>
                                    <span className="text-xs font-700" style={{ color: '#0D0D0D' }}>{tutor.rating}</span>
                                </div>
                                <span className="text-xs font-600" style={{ color: '#888' }}>{tutor.sessions} sessions</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* How it works — horizontal asymmetric */}
                <div className={`rounded-3xl p-8 md:p-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ background: 'white', border: '1px solid rgba(55,87,255,0.08)' }}>
                    <p className="text-xs font-700 uppercase tracking-widest mb-8" style={{ color: '#999', letterSpacing: '0.15em' }}>How it works</p>
                    <div className="grid md:grid-cols-4 gap-6">
                        {howItWorks.map((step, i) => (
                            <div key={step.step} className="relative">
                                {i < howItWorks.length - 1 && (
                                    <div className="hidden md:block absolute top-4 left-full w-full h-px" style={{ background: 'rgba(55,87,255,0.12)', transform: 'translateX(-50%)' }} />
                                )}
                                <div
                                    className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-800 mb-4"
                                    style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}
                                >
                                    {step.step}
                                </div>
                                <p className="text-sm font-800 mb-2" style={{ color: '#0D0D0D' }}>{step.title}</p>
                                <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
